import { useState } from 'react';
import { BanIcon, EllipsisVerticalIcon, FlagIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@rocco/utils/cn';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@rocco/components/dropdown-menu';
import { Spinner } from '@rocco/components/spinner';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';
import { useBlockUser } from '~/modules/user';
import { useConversation } from '../../context';
import type { Participant } from '../../types';
import { getParticipantName } from '../../utils';
import { UserReportDialog } from './UserReportDialog';

/**
 * The dropdown menu for a participant in the chat settings "Members" tab.
 * @see {@link ChatSettingsMembersList}
 */
export function ChatSettingsMembersDropdown({
    className,
    conversationID,
    participant,
    isDisabled = false,
}: {
    className?: string;
    conversationID: number;
    participant: Participant;
    isDisabled?: boolean;
}) {
    const participantName = getParticipantName(participant);
    const { professionalID } = useConversation();
    const isCompanyInbox = !!professionalID && professionalID > 0;
    const isParticipantBlocked =
        participant.Identity.__typename === 'Member' &&
        participant.Identity.IsBlocked;
    const [isOpen, setIsOpen] = useState(false);
    const [isReportOpen, setIsReportOpen] = useState(false);
    const [isBlockOpen, setIsBlockOpen] = useState(false);
    const { isSubmitting, blockUser, unblockUser } = useBlockUser({
        onBlockSuccess: () => {
            toast.success(`User "${participantName}" has been blocked`);
        },
        onBlockError: () => {
            toast.error(`Error blocking user "${participantName}"`);
        },
        onUnblockSuccess: () => {
            toast.success(`User "${participantName}" has been unblocked`);
        },
        onUnblockError: () => {
            toast.error(`Error unblocking user "${participantName}"`);
        },
    });

    if (participant.Identity.__typename === 'Professional') {
        // Cannot block or report professionals.
        return null;
    }

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
                <DropdownMenuTrigger asChild>
                    <button
                        className={cn(
                            'flex size-[32px] items-center justify-center rounded-full text-black/70 hover:bg-black/10 disabled:pointer-events-none disabled:opacity-50',
                            className,
                            // Keep button visible when dropdown is open.
                            isOpen && 'visible',
                        )}
                        disabled={isDisabled || isSubmitting}
                    >
                        {isSubmitting ? (
                            <Spinner className="size-[16px]" />
                        ) : (
                            <EllipsisVerticalIcon className="size-[16px]" />
                        )}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-unset min-w-40" align="end">
                    <DropdownMenuItem
                        className="focus:bg-black/10"
                        onClick={() => setIsReportOpen(true)}
                    >
                        <FlagIcon className="size-[14px]" />
                        Report
                    </DropdownMenuItem>
                    {!isCompanyInbox && (
                        <DropdownMenuItem
                            className="focus:bg-black/10"
                            onClick={() => setIsBlockOpen(true)}
                        >
                            <BanIcon className="size-[14px]" />
                            {isParticipantBlocked ? 'Unblock' : 'Block'}
                        </DropdownMenuItem>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
            <UserReportDialog
                open={isReportOpen}
                conversationID={conversationID}
                participant={participant}
                onOpenChange={setIsReportOpen}
            />
            {!isCompanyInbox && (
                <AlertDialog
                    controlled={{ open: isBlockOpen, setOpen: setIsBlockOpen }}
                    title={isParticipantBlocked ? 'Unblock user' : 'Block user'}
                    description={`Are you sure you want to ${isParticipantBlocked ? 'unblock' : 'block'} ${participantName}?`}
                    onConfirm={() => {
                        if (participant.Identity.__typename !== 'Member') {
                            return;
                        }
                        if (isParticipantBlocked) {
                            unblockUser(participant.Identity.ID);
                        } else {
                            blockUser(participant.Identity.ID);
                        }
                    }}
                />
            )}
        </>
    );
}
